import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  People,
  DriveEta,
  DirectionsCar,
  AttachMoney,
  Warning
} from '@mui/icons-material';
import axios from 'axios';

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: number;
  icon: React.ReactNode;
  color: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, change, icon, color }) => (
  <Card elevation={2}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" component="div" fontWeight="bold">
            {value}
          </Typography>
          {change !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              {change >= 0 ? (
                <TrendingUp fontSize="small" sx={{ color: 'success.main', mr: 0.5 }} />
              ) : (
                <TrendingDown fontSize="small" sx={{ color: 'error.main', mr: 0.5 }} />
              )}
              <Typography
                variant="body2"
                sx={{ color: change >= 0 ? 'success.main' : 'error.main' }}
              >
                {Math.abs(change).toFixed(1)}% from yesterday
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            bgcolor: color,
            borderRadius: 2,
            p: 1.5,
            color: 'white'
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

interface DashboardMetrics {
  users: {
    total: number;
    newToday: number;
    change: number;
    activeDrivers: number;
    activePassengers: number;
  };
  rides: {
    total: number;
    today: number;
    change: number;
    active: number;
    completedToday: number;
    cancelledToday: number;
  };
  revenue: {
    total: number;
    today: number;
    change: number;
  };
  adminTasks: {
    pendingVerifications: number;
    openDisputes: number;
    openSupportTickets: number;
  };
}

const DashboardPage: React.FC = () => {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchMetrics = async () => {
    try {
      const apiUrl = process.env.NODE_ENV === 'production'
        ? 'https://rideclubnet.com/api'
        : 'http://localhost:3001/api';
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${apiUrl}/admin/dashboard/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetrics(response.data.metrics);
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !metrics) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Dashboard Overview
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Welcome back! Here's what's happening with your platform today.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Metrics Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 3 }}>
          <MetricCard
            title="Total Users"
            value={metrics.users.total.toLocaleString()}
            change={metrics.users.change}
            icon={<People />}
            color="#667eea"
          />
          <MetricCard
            title="Rides Today"
            value={metrics.rides.today}
            change={metrics.rides.change}
            icon={<DirectionsCar />}
            color="#764ba2"
          />
          <MetricCard
            title="Active Drivers"
            value={metrics.users.activeDrivers}
            icon={<DriveEta />}
            color="#f093fb"
          />
          <MetricCard
            title="Revenue Today"
            value={`$${metrics.revenue.today.toFixed(2)}`}
            change={metrics.revenue.change}
            icon={<AttachMoney />}
            color="#4facfe"
          />
        </Box>

        {/* Admin Tasks Alert */}
        <Paper sx={{ p: 3, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Warning fontSize="large" />
            <Box>
              <Typography variant="h6" gutterBottom>
                Pending Admin Tasks
              </Typography>
              <Typography variant="body2">
                {metrics.adminTasks.pendingVerifications} driver verifications •{' '}
                {metrics.adminTasks.openDisputes} open disputes •{' '}
                {metrics.adminTasks.openSupportTickets} support tickets
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Statistics Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Ride Statistics
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Total Rides
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {metrics.rides.total.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Active Rides
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="primary">
                  {metrics.rides.active}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Completed Today
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="success.main">
                  {metrics.rides.completedToday}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Cancelled Today
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="error.main">
                  {metrics.rides.cancelledToday}
                </Typography>
              </Box>
            </Box>
          </Paper>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              User Statistics
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  New Users Today
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {metrics.users.newToday}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Active Drivers (7 days)
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {metrics.users.activeDrivers}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Active Passengers (7 days)
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {metrics.users.activePassengers}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Total Revenue
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="success.main">
                  ${metrics.revenue.total.toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardPage;
